import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { useCookies } from 'react-cookie';

export const RECENTLY_VIEWED_PRODUCTS_LIMIT = 8;

const RECENTLY_VIEWED_PRODUCTS_COOKIE_NAME = 'aow_recently_viewed_products';
const RECENTLY_VIEWED_PRODUCTS_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 90;
const FIRST_RECENTLY_VIEWED_PRODUCT_INDEX = 0;
const EMPTY_RECENTLY_VIEWED_PRODUCT_IDS: Array<number | string> = [];
const unsubscribeFromHydrationState = () => undefined;
const subscribeToHydrationState = () => unsubscribeFromHydrationState;
const getClientHydrationState = () => true;
const getServerHydrationState = () => false;
interface RecentlyViewedProductsCookie {
  [RECENTLY_VIEWED_PRODUCTS_COOKIE_NAME]?: string
}

const normalizeRecentlyViewedProductIds = (productIds: Array<number | string>): string[] => {
  const normalizedProductIds: string[] = [];
  const addedProductIds = new Set<string>();

  productIds.forEach(productId => {
    const normalizedProductId = String(productId).trim();

    if (!/^\d+$/.test(normalizedProductId) || addedProductIds.has(normalizedProductId)) {
      return;
    }

    normalizedProductIds.push(normalizedProductId);
    addedProductIds.add(normalizedProductId);
  });

  return normalizedProductIds.slice(FIRST_RECENTLY_VIEWED_PRODUCT_INDEX, RECENTLY_VIEWED_PRODUCTS_LIMIT);
}

const parseRecentlyViewedProductIds = (storedValue: string) => (
  normalizeRecentlyViewedProductIds(storedValue.split(','))
);

const getRecentlyViewedProductIds = (
  storedValue: string | undefined,
  fallbackProductIds: Array<number | string> = [],
): string[] => {
  return typeof storedValue === 'string'
    ? parseRecentlyViewedProductIds(storedValue)
    : normalizeRecentlyViewedProductIds(fallbackProductIds);
}

export function useRecentlyViewedProducts(fallbackProductIds: Array<number | string> = EMPTY_RECENTLY_VIEWED_PRODUCT_IDS) {
  const [cookies, setCookie, , updateCookies] = useCookies<
    typeof RECENTLY_VIEWED_PRODUCTS_COOKIE_NAME,
    RecentlyViewedProductsCookie
  >([RECENTLY_VIEWED_PRODUCTS_COOKIE_NAME], {
    doNotParse: true,
  });
  const storedProductIds = cookies[RECENTLY_VIEWED_PRODUCTS_COOKIE_NAME];
  const canUseBrowserCookie = useSyncExternalStore(
    subscribeToHydrationState,
    getClientHydrationState,
    getServerHydrationState,
  );
  const fallbackRecentlyViewedProductIds = useMemo(() => (
    normalizeRecentlyViewedProductIds(fallbackProductIds)
  ), [fallbackProductIds]);
  const cookieRecentlyViewedProductIds = useMemo(() => (
    getRecentlyViewedProductIds(storedProductIds, fallbackProductIds)
  ), [fallbackProductIds, storedProductIds]);

  // React uses the server snapshot for the first hydration render, then switches
  // to the browser cookie after hydration. Client-only renders use the cookie immediately.
  const recentlyViewedProductIds = canUseBrowserCookie
    ? cookieRecentlyViewedProductIds
    : fallbackRecentlyViewedProductIds;

  const addRecentlyViewedProduct = useCallback((productId: number | string) => {
    const normalizedProductIds = normalizeRecentlyViewedProductIds([productId]);
    if (normalizedProductIds.length === 0) return;

    const [normalizedProductId] = normalizedProductIds;
    const nextRecentlyViewedProductIds = normalizeRecentlyViewedProductIds([
      normalizedProductId,
      ...cookieRecentlyViewedProductIds.filter(existingProductId => existingProductId !== normalizedProductId),
    ]);

    setCookie(
      RECENTLY_VIEWED_PRODUCTS_COOKIE_NAME,
      nextRecentlyViewedProductIds.join(','),
      {
        maxAge: RECENTLY_VIEWED_PRODUCTS_COOKIE_MAX_AGE_SECONDS,
        path: '/',
        sameSite: 'lax',
      },
    );
  }, [cookieRecentlyViewedProductIds, setCookie]);

  const refreshRecentlyViewedProducts = useCallback(() => {
    updateCookies();
  }, [updateCookies]);

  return {
    addRecentlyViewedProduct,
    recentlyViewedProductIds,
    refreshRecentlyViewedProducts,
  };
}
